import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import {
  useCreateUserProfileTypeMutation,
  useGetDataTemplateByIdQuery,
  useGetProfileTypesQuery,
  useGetUserProfileTypesQuery,
  usePatchUserProfileTypeMutation,
  useUpdateUserProfileTypeMutation,
} from "@/redux/service";
import {
  DataBlockFormFields,
  ProfileTypeDisplayProps,
  ProfileTypeFormProps,
  ProfileTypeFormsProps,
  ProfileTypeFormStateProps,
  ProfileTypeFormSubmitFunction,
  UserProfileTypeWithProfileTypeData,
} from "@/features/users/profile/types";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import { useDataBlockSubmission } from "@/domains/datablocks/hooks/useDataBlockSubmission";
import { useDataBlockValidation } from "@/domains/datablocks/hooks/useDataBlockValidation";
import { useDataBlockForm } from "@/domains/datablocks/hooks/useDataBlockForm";
import { DataBlockProvider } from "@/domains/datablocks/providers/DataBlockProvider";
import {
  DisplayFieldValue,
  FormFieldWithName,
} from "@/domains/datablocks/types";
import { handleErrors } from "@/shared/utils/forms";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { useNavigationBlocker } from "@/shared/hooks/useNavigationBlocker";
import { useDataBlockDependencies } from "@/domains/datablocks/hooks/useDataBlockDependencies";
import FieldRenderer from "@/domains/datablocks/fields/FieldRenderer";
import {
  DataBlockResponse,
  DataFieldResponse,
  DataTemplateResponse,
  FieldValueResponse,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";
import { ProfileTypeResponse } from "@/redux/users/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { getContentItemForField } from "@/domains/datablocks/fields/renderers";
import { BUTTON_SIZES } from "@/components_v2/Button/constants";

const ProfileTypeDisplay = ({
  dataBlock,
  dataTemplateId,
}: ProfileTypeDisplayProps) => {
  const { data: dataTemplate } = useGetDataTemplateByIdQuery(dataTemplateId);

  const fieldValuesMap = dataBlock.fieldValues.reduce<
    Record<number, FieldValueResponse>
  >((acc, field) => {
    acc[field?.field] = field;
    return acc;
  }, {});

  const allFields: DisplayFieldValue[] = useMemo(() => {
    if (!dataTemplate) return [];

    return dataTemplate.templateFields
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((templateField) => {
        const fieldValue = fieldValuesMap[templateField.id];
        return {
          field: templateField,
          value: fieldValue?.value,
        };
      });
  }, [dataTemplate]);

  return allFields.map((fieldValue) => getContentItemForField(fieldValue));
};

const ProfileTypeForm = ({
  dataTemplateId,
  dataBlock,
  onRegisterSubmit,
}: ProfileTypeFormProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const fieldRefs = useRef<Record<string, React.RefObject<HTMLElement | null>>>(
    {},
  );
  const { data: dataTemplate, isSuccess: isTemplateSuccess } =
    useGetDataTemplateByIdQuery(dataTemplateId);
  const currentUser = useSelector(selectCurrentUser);
  const dataBlockDependencies = useDataBlockDependencies(dataTemplate);
  const { handleSubmit, isSubmitting } = useDataBlockSubmission(currentUser);
  const { validationSchema, updateSchemaForTemplate } =
    useDataBlockValidation();
  const { form, orderFieldValuesAccordingToTemplateFields } =
    useDataBlockForm(validationSchema);
  useNavigationBlocker(
    form.formState.isDirty,
    `profile-type-form-${dataBlock.id}`,
  );
  const templateFieldsMapping = useMemo(() => {
    if (!dataTemplate) return null;

    return dataTemplate.templateFields.reduce<
      Record<number, DataFieldResponse>
    >((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [dataTemplate]);

  useEffect(() => {
    if (templateFieldsMapping && dataBlock && dataTemplate) {
      const data = getInitialDataForForm(dataTemplate, dataBlock);
      form.reset(data);
    }
  }, [templateFieldsMapping, dataTemplate, dataBlock]);

  useEffect(() => {
    if (isTemplateSuccess && dataTemplate) {
      updateSchemaForTemplate(dataTemplate);
    }
  }, [dataTemplate, isTemplateSuccess]);

  const fields: FormFieldWithName[] = useMemo(() => {
    if (!dataTemplate) return [];

    return dataTemplate.templateFields.map((i: DataFieldResponse, index) => {
      fieldRefs.current[`fieldValues.${index}.value`] =
        createRef<HTMLElement>();

      return {
        ...i,
        name: `fieldValues.${index}.value`,
        ref: fieldRefs.current[`fieldValues.${index}.value`],
      };
    });
  }, [dataTemplate]);

  const scrollToFirstError = useCallback(() => {
    const errors = form.formState.errors;
    if (!errors.fieldValues) return false;

    let firstErrorIndex = -1;

    if (Array.isArray(errors.fieldValues)) {
      firstErrorIndex = errors.fieldValues.findIndex((err) => err?.value);
    } else {
      for (let i = 0; i < fields.length; i++) {
        if (errors.fieldValues[i]?.value) {
          firstErrorIndex = i;
          break;
        }
      }
    }

    if (firstErrorIndex >= 0) {
      const fieldName = `fieldValues.${firstErrorIndex}.value`;
      const fieldRef = fieldRefs.current[fieldName];

      if (fieldRef?.current) {
        fieldRef.current.scrollTo({ behavior: "smooth" });
        if (fieldRef.current.focus) fieldRef.current.focus();
        return true;
      }
    }
    return false;
  }, [form.formState.errors, fields]);

  useEffect(() => {
    const submitFunction = async (
      customSubmitFn: ProfileTypeFormSubmitFunction,
    ) => {
      const isValid = await form.trigger("fieldValues");
      if (!isValid) {
        setTimeout(() => scrollToFirstError(), 100);
        return;
      }
      if (!currentUser) return;

      return handleSubmit(
        form.getValues(),
        customSubmitFn,
        (response) => {
          if (dataTemplate) {
            const data = getInitialDataForForm(
              dataTemplate,
              response.dataBlock,
            );
            form.reset(data, { keepDirty: false });
          }
          notification.success({
            message: t("users.labels.profileTypeFormSaved"),
          });
        },
        (errors) => {
          handleErrors(errors, form.setError, notification);
        },
      );
    };
    onRegisterSubmit(
      dataBlock.id,
      submitFunction,
      isSubmitting,
      form.formState.isDirty,
    );
  }, [
    currentUser,
    isSubmitting,
    dataBlock.id,
    form.formState.isDirty,
    scrollToFirstError,
  ]);

  const getInitialDataForForm = (
    dataTemplate: DataTemplateResponse,
    dataBlock: DataBlockResponse,
  ) => {
    const values = dataBlock.fieldValues.map((fieldValue) => ({
      field: fieldValue.field,
      value: fieldValue.value,
    }));
    const fieldOrder = dataTemplate.templateFields.map((field) => field.id);
    const orderedValues = orderFieldValuesAccordingToTemplateFields(
      fieldOrder,
      values,
    );
    const existingValues = orderedValues.map((fieldValue) => ({
      field: fieldValue.field,
      value: fieldValue.value,
    }));
    const allFieldValues = dataTemplate.templateFields.map((field) => {
      const existingValue = existingValues.find((v) => v.field === field.id);
      return existingValue || { field: field.id, value: undefined };
    });
    return {
      dataTemplate: dataBlock.template.id,
      sharingRights: dataBlock.sharingRights,
      fieldValues: allFieldValues,
    };
  };

  const eitherRequiredRules: Record<number, -1 | 0 | 1> = useMemo(() => {
    if (!dataTemplate) return [];

    const rules = dataTemplate.validationRules
      .filter((rule) => rule.type === ValidationRuleTypes.EITHER_REQUIRED)
      .map((rule) => rule.ruleFields);

    const orderFieldIds = fields.map((field) => field.id);

    const orderedRules = rules.map((rule) =>
      rule
        .slice()
        .sort((a, b) => orderFieldIds.indexOf(a) - orderFieldIds.indexOf(b)),
    );

    return orderedRules.reduce(
      (acc, fieldIds) => {
        fieldIds.forEach((fieldId: number) => {
          const fieldIndex = fieldIds.indexOf(fieldId);
          acc[fieldId] =
            fieldIndex === 0 ? -1 : fieldIndex === fieldIds.length - 1 ? 1 : 0;
        });
        return acc;
      },
      {} as Record<number, -1 | 0 | 1>,
    );
  }, [dataTemplate, fields]);

  if (!dataTemplate) return <BaseSpinner />;

  return (
    <div className="flex flex-col gap-300">
      {fields.map((field) => (
        <FieldRenderer
          key={`field-${field.id}-${dataTemplate.id}`}
          field={field}
          control={form.control}
          dataBlockDependencies={dataBlockDependencies}
          eitherRequiredRules={eitherRequiredRules}
          ref={fieldRefs.current[field.name]}
        />
      ))}
    </div>
  );
};

const ProfileTypeForms = ({ user }: ProfileTypeFormsProps) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const { data: profileTypes } = useGetProfileTypesQuery({ pagination: "off" });
  const { data: userProfileTypes } = useGetUserProfileTypesQuery({
    user: user.id,
    pagination: "off",
  });
  const currentUser = useSelector(selectCurrentUser);
  const [createUserProfileType] = useCreateUserProfileTypeMutation();
  const [updateUserProfileType] = useUpdateUserProfileTypeMutation();
  const [patchUserProfileType] = usePatchUserProfileTypeMutation();
  const [formStates, setFormStates] = useState<ProfileTypeFormStateProps>({});

  const isOwnProfile = useMemo(
    () => currentUser && currentUser.id === user.id,
    [currentUser, user],
  );

  const registerSubmit = useCallback(
    (
      blockId: number,
      submitFn: (innerSubmitFn: ProfileTypeFormSubmitFunction) => void,
      isSubmitting: boolean,
      isFormDirty: boolean,
    ) => {
      setFormStates((prev) => ({
        ...prev,
        [blockId.toString()]: { submitFn, isSubmitting, isFormDirty },
      }));
    },
    [],
  );

  const handleFormSubmit = useCallback(
    (dataBlockId: number) => {
      if (!isOwnProfile) return;

      const formState = formStates[dataBlockId.toString()];
      if (formState && userProfileTypes) {
        const userProfileType = userProfileTypes.find(
          (userProfileType) =>
            userProfileType.dataBlock &&
            userProfileType.dataBlock.id === dataBlockId,
        );
        if (!userProfileType) return;
        formState.submitFn(async (data: DataBlockFormFields) => {
          return await patchUserProfileType({
            id: userProfileType.id,
            data: { dataBlock: data },
          }).unwrap();
        });
      }
    },
    [isOwnProfile, formStates, userProfileTypes],
  );

  const handleProfileTypeChange = (checked: boolean, typeId: number) => {
    if (!userProfileTypes) return;
    const userProfileType = userProfileTypes.find(
      (userProfileType) => userProfileType.type === typeId,
    );
    if (userProfileType) {
      updateUserProfileType({
        id: userProfileType.id,
        data: { type: typeId, isActive: checked },
      })
        .unwrap()
        .then(() => {
          notification.success({ message: t("users.labels.profileTypeSaved") });
        });
    } else {
      createUserProfileType({ type: typeId, isActive: checked })
        .unwrap()
        .then(() => {
          notification.success({ message: t("users.labels.profileTypeSaved") });
        });
    }
  };

  const getIsProfileTypeSelected = (profileTypeId: number) => {
    if (!userProfileTypes) return;

    const type = userProfileTypes.find(
      (userProfileType) => userProfileType.type === profileTypeId,
    );
    return type && type.isActive;
  };

  const nonDefaultProfileTypes = useMemo(() => {
    if (!profileTypes) return [];
    return profileTypes.filter((profileType) => !profileType.isDefault);
  }, [profileTypes]);

  const activeProfileForms = useMemo(() => {
    if (!profileTypes || !userProfileTypes) return [];

    return userProfileTypes
      .filter(
        (userProfileType) =>
          userProfileType.isActive && userProfileType.dataBlock != undefined,
      )
      .reduce<UserProfileTypeWithProfileTypeData[]>((acc, userProfileType) => {
        const profileType = profileTypes.find(
          (profileType) => profileType.id === userProfileType.type,
        );
        if (profileType) {
          acc.push({
            profileType: {
              ...profileType,
              dataTemplate: profileType.dataTemplate as number,
            },
            ...userProfileType,
            dataBlock: userProfileType.dataBlock as DataBlockResponse,
          });
        }
        return acc;
      }, []);
  }, [profileTypes, userProfileTypes]);

  return (
    <div className="flex flex-col gap-400 full-width">
      {isOwnProfile && (
        <div className="profile-type-form-question flex flex-col gap-200 items-start self-stretch justify-center">
          <div>
            <div className="flex flex-col gap-100 items-start self-stretch justify-center">
              <h3 className="header-m-medium">
                {t("users.labels.profileTypeQuestionLabel")}
              </h3>
              <p>{t("users.labels.selectAllIfAny")}</p>
            </div>

            {userProfileTypes ? (
              nonDefaultProfileTypes.map((type: ProfileTypeResponse) => (
                <BaseCheckbox
                  key={type.id}
                  label={type.name}
                  checked={getIsProfileTypeSelected(type.id)}
                  onChange={(checked) =>
                    handleProfileTypeChange(checked, type.id)
                  }
                />
              ))
            ) : (
              <BaseSpinner />
            )}
          </div>
        </div>
      )}

      <BaseAccordion
        className="profile-type-form-accordions"
        items={activeProfileForms.map((form) => {
          const formState = formStates[form.dataBlock.id.toString()];
          return {
            key: form.id,
            label: (
              <div>
                <h3 className="header-m-medium">{form.profileType.name}</h3>
                {formState && isOwnProfile ? (
                  <BaseButton
                    size={BUTTON_SIZES.SMALL}
                    loading={formState?.isSubmitting}
                    disabled={!formState.isFormDirty}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFormSubmit(form.dataBlock.id);
                    }}
                  >
                    {t("buttons.save")}
                  </BaseButton>
                ) : null}
              </div>
            ),
            children: isOwnProfile ? (
              <DataBlockProvider>
                <ProfileTypeForm
                  dataTemplateId={form.profileType.dataTemplate}
                  dataBlock={form.dataBlock}
                  onRegisterSubmit={registerSubmit}
                />
              </DataBlockProvider>
            ) : (
              <ProfileTypeDisplay
                dataBlock={form.dataBlock}
                dataTemplateId={form.profileType.dataTemplate}
              />
            ),
          };
        })}
      />
    </div>
  );
};

export default ProfileTypeForms;
